class ProductTabs extends HTMLElement {
  #controller = new AbortController();
  #mobileBreakpoint = 749;

  connectedCallback() {
    this.#buildTabs();
    this.#handleResize();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #isMobile() {
    return window.innerWidth <= this.#mobileBreakpoint;
  }

  #handleResize() {
    const { signal } = this.#controller;
    let wasMobile = this.#isMobile;

    window.addEventListener('resize', () => {
      const isMobile = this.#isMobile;
      if (isMobile !== wasMobile) {
        wasMobile = isMobile;
        this.#applyMode(isMobile);
      }
    }, { signal });

    this.#applyMode(wasMobile);
  }

  #applyMode(isMobile) {
    const rows = this.querySelectorAll('accordion-custom');

    if (isMobile) {
      rows.forEach((row) => {
        row.classList.remove('product-tabs__panel--active');
        const details = row.querySelector('details');
        if (details) details.open = false;
      });
    } else {
      this.#activateTab(0);
    }
  }

  #buildTabs() {
    const { signal } = this.#controller;
    const nav = this.querySelector('.product-tabs__nav');
    const rows = this.querySelectorAll('accordion-custom');

    if (!nav || rows.length === 0) return;

    rows.forEach((row, index) => {
      const summary = row.querySelector('.details__header');
      const heading = summary ? summary.textContent.trim() : `Tab ${index + 1}`;

      const button = document.createElement('button');
      button.className = 'product-tabs__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.textContent = heading;

      if (index === 0) {
        button.classList.add('product-tabs__tab--active');
      }

      button.addEventListener('click', () => {
        if (!this.#isMobile) this.#activateTab(index);
      }, { signal });
      button.addEventListener('keydown', (e) => this.#handleKeyDown(e, index), { signal });

      nav.appendChild(button);
    });
  }

  /** @param {number} activeIndex */
  #activateTab(activeIndex) {
    const tabs = this.querySelectorAll('.product-tabs__tab');
    const rows = this.querySelectorAll('accordion-custom');

    tabs.forEach((tab, i) => {
      const isActive = i === activeIndex;
      tab.classList.toggle('product-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    rows.forEach((row, i) => {
      const isActive = i === activeIndex;
      row.classList.toggle('product-tabs__panel--active', isActive);
      const details = row.querySelector('details');
      if (details) {
        details.open = isActive;
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   */
  #handleKeyDown(event, currentIndex) {
    if (this.#isMobile) return;

    const tabs = [...this.querySelectorAll('.product-tabs__tab')];
    const count = tabs.length;

    let newIndex;
    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % count;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + count) % count;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(newIndex);
    tabs[newIndex].focus();
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
